// FILE: src/components/admin/download.js
// Server-made files (WP-7: student records, bulk exports, daily hours). The
// file is fetched with the session cookie, saved under the name the server
// gives in Content-Disposition, and the record's SHA-256 fingerprint
// (X-Record-SHA256) is handed back so the page can show it (MTO 11.1, matrix
// 5.6). A refusal rejects like fetchWithAuth: an Error with `code` and the
// JSON `body`, so pages show it with errorMessage(err).
import { apiFetch } from "../../services/api";

/** `attachment; filename="a.pdf"` (or RFC 5987 filename*=) → "a.pdf"; null when absent. */
export function filenameFromDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      /* fall through to the plain name */
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  if (!plain) return null;
  const name = (plain[2] !== undefined ? plain[2] : plain[1]).trim();
  // Never let a header put a path in the saved name.
  return name ? name.replace(/[/\\]/g, "_") : null;
}

/** Saves a Blob as `filename` through a temporary link (works with every browser we support). */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function failure(res) {
  let body = null;
  try {
    body = JSON.parse(await res.text());
  } catch {
    /* not JSON */
  }
  const code = body && typeof body.code === "string" ? body.code : res.status === 404 ? "NOT_FOUND" : "INTERNAL_ERROR";
  const err = new Error(code);
  err.code = code;
  err.body = body || { code };
  err.status = res.status;
  return err;
}

/**
 * GET `path` as a file and save it. → { filename, sha256, size, type, blob }.
 * `fallbackName` is used when the server sends no file name.
 */
export async function downloadFromApi(path, { fallbackName = "download" } = {}) {
  const res = await apiFetch(path);
  if (!res.ok) throw await failure(res);
  const blob = await res.blob();
  const headers = res.headers;
  const filename = filenameFromDisposition(headers?.get?.("Content-Disposition")) || fallbackName;
  saveBlob(blob, filename);
  return {
    filename,
    sha256: headers?.get?.("X-Record-SHA256") || null,
    size: typeof blob.size === "number" ? blob.size : null,
    type: blob.type || headers?.get?.("Content-Type") || null,
    blob
  };
}

/** 1536 → "1.5 KB" (for "Downloaded … (1.5 KB)"). */
export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "";
  if (v < 1024) return `${v} bytes`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}
